import { useState } from 'react';
import cn from 'classnames';
import {
  MdFlag,
  MdFolderCopy,
  MdGpsFixed,
  MdLightbulb,
  MdPaid,
  MdPlayArrow,
  MdRoute,
  MdSchedule,
  MdUndo
} from 'react-icons/md';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { FocusChart, QuadrantBubbleChart, SetLinesChart } from './CreativeSuiteCharts';
import { MetricGrid, SectionCard, SrcCue } from './CreativeSuiteShared';
import {
  BubbleColorMode,
  BubbleColorModeType,
  COMBINED_METRICS,
  FOCUS_SET_LABEL,
  HEALTHY_HIDDEN_COUNT,
  PLAYBACK_DATE_LABEL,
  PLAYBACK_PROGRESS,
  PLAYBACK_WEEK_LABEL,
  SET_LINES
} from './creativeSuite-constants';

const OverTimeLegend = () => (
  <div className='legend-row'>
    {SET_LINES.map(line => (
      <span className='lg' key={line.key}>
        <span className='sw' style={{ background: line.color }} />
        {line.label}
      </span>
    ))}
    <span className='lg faded'>
      <span className='sw swatch-muted' />
      {HEALTHY_HIDDEN_COUNT} healthy sets hidden
    </span>
    <span className='lg legend-hint'>
      <MdFlag aria-hidden /> ▲ = ad went live
    </span>
  </div>
);

const FocusLegend = () => (
  <div className='legend-row'>
    <span className='lg'>
      <span className='sw sw-fatigue' />
      Fatigue
    </span>
    <span className='lg'>
      <span className='sw sw-diversification' />
      Diversification
    </span>
    <span className='lg'>
      <span className='sw sw-roas' />
      ROAS
    </span>
    <span className='lg'>
      <span className='sw sw-cpa' />
      CPA
    </span>
  </div>
);

const BubbleColorToggle = ({
  mode,
  onChange
}: {
  mode: BubbleColorModeType;
  onChange: (mode: BubbleColorModeType) => void;
}) => (
  <div className='seg'>
    <InteractiveSpan
      className={cn('seg-item', { on: mode === BubbleColorMode.ROAS })}
      onClick={() => onChange(BubbleColorMode.ROAS)}
    >
      <MdPaid aria-hidden /> ROAS
    </InteractiveSpan>
    <InteractiveSpan
      className={cn('seg-item', { on: mode === BubbleColorMode.CAMPAIGN })}
      onClick={() => onChange(BubbleColorMode.CAMPAIGN)}
    >
      <MdFolderCopy aria-hidden /> Campaign
    </InteractiveSpan>
  </div>
);

export const CombinedView = () => {
  const [bubbleMode, setBubbleMode] = useState<BubbleColorModeType>(BubbleColorMode.ROAS);

  return (
    <div className='bp-panel'>
      <MetricGrid metrics={COMBINED_METRICS} columns={4} />

      <SectionCard
        title='Fatigue over time · by ad set'
        sub='Healthy sets (high diversity, low fatigue) hidden by default · click a line to isolate'
        right={<SrcCue>Live from Meta API</SrcCue>}
        className='stack-card'
      >
        <OverTimeLegend />
        <div className='chart-frame chart-frame-sm'>
          <SetLinesChart />
        </div>
      </SectionCard>

      <SectionCard className='stack-card'>
        <div className='focus-banner'>
          <MdGpsFixed aria-hidden className='focus-icon' />
          <div className='focus-text'>
            <b>Isolated: {FOCUS_SET_LABEL}</b> · metrics + performance overlaid
          </div>
          <InteractiveSpan className='mini-btn'>
            <MdUndo aria-hidden /> Back to all sets
          </InteractiveSpan>
        </div>
        <FocusLegend />
        <div className='chart-frame chart-frame-sm'>
          <FocusChart />
        </div>
      </SectionCard>

      <SectionCard
        title='Fatigue × Diversity quadrant'
        sub='Bubble = ad set · size = spend'
        right={<BubbleColorToggle mode={bubbleMode} onChange={setBubbleMode} />}
      >
        <div className='chart-frame chart-frame-lg'>
          <QuadrantBubbleChart colorMode={bubbleMode} />
        </div>
        <div className='ctx-note'>
          <MdLightbulb aria-hidden />
          <div>
            <b>Aim for the bottom-right</b> — high diversification, low fatigue. Diversification is
            weighted more heavily than fatigue: a diverse set resists fatigue on its own, so pushing
            left-to-right matters more than pushing a single set down.
          </div>
        </div>
        <div className='play-bar'>
          <span className='play-btn'>
            <MdPlayArrow aria-hidden />
          </span>
          <div className='play-week'>{PLAYBACK_WEEK_LABEL}</div>
          <div className='scrub'>
            <div className='fill' style={{ width: `${PLAYBACK_PROGRESS * 100}%` }} />
            <div className='knob' style={{ left: `${PLAYBACK_PROGRESS * 100}%` }} />
          </div>
          <span className='play-date'>
            <MdSchedule aria-hidden /> {PLAYBACK_DATE_LABEL}
          </span>
          <InteractiveSpan className='mini-btn'>
            <MdRoute aria-hidden /> Trails
          </InteractiveSpan>
        </div>
      </SectionCard>
    </div>
  );
};
